/**
 * 滚动动画组件
 * 简洁的滚动进入视口动画效果
 */

// 类型声明
/**
 * @typedef {Object} ScrollAnimationOptions
 * @property {number} threshold
 * @property {string} rootMargin
 * @property {string} animationClass
 */

/**
 * @typedef {Object} IntersectionObserverEntry
 * @property {boolean} isIntersecting
 * @property {Element} target
 */
class ScrollAnimation {
  constructor(options = {}) {
    this.options = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
      animationClass: 'is-visible',
      ...options
    };
    
    this.observer = null;
    this.init();
  }

  init() {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('IntersectionObserver is not supported');
      return;
    }

    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      this.options
    );
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add(this.options.animationClass);
        // 动画触发后停止观察该元素
        if (this.observer) {
          this.observer.unobserve(entry.target);
        }
      }
    });
  }

  /**
   * 观察指定容器内的元素
   * @param {string|HTMLElement} container - 容器选择器或DOM元素
   * @param {string} selector - 要观察的元素选择器
   */
  observe(container, selector) {
    if (!this.observer) return;

    const containerElement = typeof container === 'string' 
      ? document.querySelector(container) 
      : container;

    if (!containerElement) {
      console.warn(`Container not found: ${container}`);
      return;
    }

    const elements = containerElement.querySelectorAll(selector);
    elements.forEach(element => {
      // 确保元素初始状态为隐藏
      element.classList.remove(this.options.animationClass);
      if (this.observer) {
        this.observer.observe(element);
      }
    });
  }

  /**
   * 观察单个元素
   * @param {string|HTMLElement} element - 元素选择器或DOM元素
   */
  observeElement(element) {
    if (!this.observer) return;

    const targetElement = typeof element === 'string' 
      ? document.querySelector(element) 
      : element;

    if (!targetElement) {
      console.warn(`Element not found: ${element}`);
      return;
    }

    targetElement.classList.remove(this.options.animationClass);
    if (this.observer) {
      this.observer.observe(targetElement);
    }
  }

  /**
   * 销毁观察器
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// 自动初始化
document.addEventListener('DOMContentLoaded', () => {
  // 全局动画实例
  /** @type {any} */
  const win = window;
  win.scrollAnimation = new ScrollAnimation();
  
  // 自动检测并观察所有带有 scroll-animate 类的元素
  const animateElements = document.querySelectorAll('.scroll-animate');
  animateElements.forEach(element => {
    element.classList.remove('is-visible');
    win.scrollAnimation.observeElement(element);
  });
  
  // 监听 DOM 变化，自动处理动态添加的元素
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          const element = /** @type {Element} */ (node);
          
          // 检查新添加的节点本身是否有 scroll-animate 类
          if (element.classList && element.classList.contains('scroll-animate')) {
            element.classList.remove('is-visible');
            win.scrollAnimation.observeElement(element);
          }
          
          // 检查新添加的节点内部是否有 scroll-animate 类的元素
          if (element.querySelectorAll) {
            const newAnimateElements = element.querySelectorAll('.scroll-animate');
            newAnimateElements.forEach(/** @type {Element} */ (element) => {
              element.classList.remove('is-visible');
              win.scrollAnimation.observeElement(element);
            });
          }
        }
      });
    });
  });
  
  // 开始观察整个文档的变化
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});

// 导出供其他模块使用
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ScrollAnimation;
}
